/**
 * AdminService - Main Export File
 * Centralized service layer for all admin Firebase operations
 */
import { UserService } from './UserService';
import { ReportService } from './ReportService';
// ===== BASE CLASSES =====
import { BaseService } from './BaseService';
export { BaseService } from './BaseService';

// ===== INDIVIDUAL SERVICES =====

export { UserService } from './UserService';
export { ReportService } from './ReportService';

// ===== TYPES & CONSTANTS =====
export * from './types';

// ===== UTILITIES =====
export * from './utils';

/**
 * AdminService Factory
 * Creates service instances with proper configuration
 */
export class AdminServiceFactory {
  constructor() {
    this.services = new Map();
    this.initializeDefaultServices();
  }

  /**
   * Initialize default service instances
   */
  initializeDefaultServices() {
    // Create default service instances
    this.services.set('users', new UserService());
    this.services.set('reports', new ReportService());
  }

  /**
   * Get a service instance by name
   * @param {string} serviceName - Name of the service
   * @param {string} collectionName - Optional collection name for custom services
   * @returns {Object} Service instance
   */
  getService(serviceName, collectionName = null) {
    if (this.services.has(serviceName)) {
      return this.services.get(serviceName);
    }

    // Create custom service if collection name provided
    if (collectionName) {
      const customService = new BaseService(collectionName);
      this.services.set(serviceName, customService);
      return customService;
    }

    throw new Error(`Service '${serviceName}' not found`);
  }

  /**
   * Get specific service instances
   */
  get userService() {
    return this.getService('users');
  }

  get reportService() {
    return this.getService('reports');
  }

  /**
   * Clear all service instances
   */
  clearServices() {
    this.services.clear();
    this.initializeDefaultServices();
  }

  /**
   * Get all available service names
   * @returns {Array} Array of service names
   */
  getAllServices() {
    return Array.from(this.services.keys());
  }

  /**
   * Check if a service exists
   * @param {string} serviceName - Name of the service
   * @returns {boolean} True if service exists
   */
  hasService(serviceName) {
    return this.services.has(serviceName);
  }
}

// Create default factory instance
export const adminServiceFactory = new AdminServiceFactory();

/**
 * Quick access to service instances
 */
export const services = {
  users: adminServiceFactory.userService,
  reports: adminServiceFactory.reportService
};

/**
 * Default export for easy importing
 */
export default {
  BaseService,
  UserService,
  ReportService,
  types: require('./types'),
  utils: require('./utils'),
  factory: adminServiceFactory,
  services,
  COLLECTIONS: require('./types').COLLECTIONS
}; 